import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import styled from 'styled-components';

import { tm } from 'utils/theme';
import { parseOptions } from 'utils/form';
import { dateValidator } from 'utils/timeProvider';

import { AppContext } from 'contexts/AppContext';
import { determineFieldName } from './utils';

const TextInput = loadable(() => import('components/Molecules/TextInput'));
const DateInput = loadable(() => import('components/Molecules/DateInput'));
const SelectInput = loadable(() => import('components/Molecules/SelectInput'));
const ChecklistField = loadable(() => import('./ChecklistField'));

const StyledTextInput = styled(TextInput)`
  && {
    grid-column: span 2;

    @media (max-width: ${tm('tablet')}) {
      grid-column: 1;
    }
  }
`;
const altIncomeTypeOptions = [
  { value: 'basePay', label: 'Base Pay' },
  { value: 'overtime', label: 'Overtime' },
  { value: 'tips', label: 'Tips' },
  { value: 'bonuses', label: 'Bonuses' },
  { value: 'anticipatedPayIncrease', label: 'Anticipated Pay Increase' },
];
export const payFrequencyOptions = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biWeekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'semiMonthly', label: 'Semi-Monthly' },
  { value: 'annually', label: 'Annually' },
];
const LinkedFields = ({ linked, preload, errors }) => {
  // This is used to store current form values for the linked fields so automatic calculations can be done
  // on other linked fields
  const getInitialLinkedState = () => {
    return linked.reduce((linkedState, link) => {
      const linkName = determineFieldName(link);
      return {
        ...linkedState,
        [linkName]: '0.00',
      };
    }, {});
  };
  const [linkedState, setLinkedState] = useState(getInitialLinkedState());
  const { states, incomeTypes } = useContext(AppContext);
  const statesOptions = parseOptions(states);
  const incomeTypeOptions = parseOptions(incomeTypes);

  return linked.map((link) => {
    const disabledField = link?.disabled ?? false;
    const onBlur = link.onBlur
      ? (e) => {
          const inputValue = e.target.value;
          const [val, updateKey, updateVal] = link.onBlur(
            inputValue,
            linkedState,
            link,
          );
          const blurredLinkName = determineFieldName(link);

          setLinkedState((prev) => ({
            ...prev,
            [blurredLinkName]: val,
            [updateKey]: updateVal,
          }));
        }
      : null;
    const linkName = determineFieldName(link);
    switch (link.type) {
      /** Value, Monthly Income, Annual Gross */
      case 'money':
        return (
          <TextInput
            key={linkName}
            name={linkName}
            label={link.label}
            placeholder={`${link.placeholder || 'Enter Amount'}`}
            prefix="$"
            type="number"
            value={link.isControlled ? linkedState[linkName] : null}
            controlled={link.isControlled}
            defaultValue={
              link.isControlled ? null : preload[linkName] ?? '0.00'
            }
            error={errors[linkName]}
            disabled={disabledField}
            onBlur={onBlur}
            onChange={link.onChange}
          />
        );

      case 'percent':
        return (
          <TextInput
            key={linkName}
            name={linkName}
            label={link.label}
            placeholder={`${link.placeholder || 'Enter Value'}`}
            prefix="%"
            type="number"
            controlled={link.isControlled}
            value={link.isControlled ? linkedState[linkName] : null}
            defaultValue={
              link.isControlled ? null : preload[linkName] ?? '0.00'
            }
            error={errors[linkName]}
            disabled={disabledField}
            onBlur={onBlur}
          />
        );

      /** Other */
      case 'text':
        return (
          <StyledTextInput
            key={linkName}
            name={linkName}
            label={link.label}
            placeholder={`${link.placeholder || ''}`}
            type="text"
            defaultValue={preload[linkName]}
            error={errors[linkName]}
            disabled={disabledField}
          />
        );

      /** Number Field */
      case 'number':
        return (
          <TextInput
            key={linkName}
            name={linkName}
            label={link.label}
            placeholder={`${link.placeholder || ''}`}
            type="number"
            defaultValue={preload[linkName]}
            error={errors[linkName]}
            disabled={disabledField}
            onChange={link.onChange}
          />
        );

      /** Date */
      case 'date':
        return (
          <DateInput
            key={linkName}
            name={linkName}
            label={link.label}
            isValidDate={(date) => dateValidator(date, link.validator)}
            defaultValue={preload[linkName]}
            error={errors[linkName]}
            openAlignRightForMobile
            disabled={disabledField}
          />
        );

      /** County */
      case 'county':
        return (
          <TextInput
            key={linkName}
            name={linkName}
            label={link.label}
            placeholder={`${link.placeholder || 'Enter County'}`}
            type="text"
            defaultValue={preload[linkName]}
            error={errors[linkName]}
            disabled={disabledField}
          />
        );

      /** State Dropdown */
      case 'state':
        return (
          <SelectInput
            key={linkName}
            name={linkName}
            label={link.label}
            options={statesOptions}
            defaultValue={preload[linkName]}
            error={errors[linkName]}
            disabled={disabledField}
          />
        );

      /** Pay Frequency Dropdown */
      case 'payFrequency':
        return (
          <SelectInput
            key={linkName}
            name={linkName}
            label={link.label}
            options={payFrequencyOptions}
            defaultValue={preload[linkName]}
            error={errors[linkName]}
            disabled={disabledField}
            onChange={link.onChange}
          />
        );

      /** Income Type Dropdown */
      case 'incomeType':
        return (
          <SelectInput
            key={linkName}
            name={linkName}
            label={link.label}
            options={incomeTypeOptions}
            defaultValue={preload[linkName]}
            error={errors[linkName]}
            disabled={disabledField}
          />
        );

      /** Alternate Employment Income Type Dropdown */
      case 'employmentIncomeType':
        return (
          <SelectInput
            key={linkName}
            name={linkName}
            label={link.label}
            options={altIncomeTypeOptions}
            defaultValue={preload[linkName]}
            error={errors[linkName]}
            disabled={disabledField}
          />
        );

      /** Nested ChecklistFields */
      case 'choice':
        return (
          <ChecklistField
            key={linkName}
            field={{
              ...link,
              name: linkName,
            }}
            preload={preload}
            errors={errors}
            disabled={disabledField}
          />
        );

      /** Error */
      default:
        console.warn(
          `[LinkedFields] Encountered unrecognized linked field type: ${link.type}`,
        );
        return null;
    }
  });
};

LinkedFields.propTypes = {
  linked: PropTypes.array.isRequired,
  preload: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
};

export default LinkedFields;
